import { call, put, SagaGenerator, select } from "typed-redux-saga";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { NEW_INSTANCE_IDENTIFIER } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { DataProvider } from "@com.mgmtp.a12.client/client-core/lib/core/data";

import { assertTrue } from "../../../utilities/assert";
import { addDataHolder, createCustomNotificationAction } from "../../../app/actions";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { ContactData } from "../../contactData/types";
import { loadContactDataByClients } from "../../contactData/utils";
import { selectAvailableClients } from "../../../utilities/selectors/modulfDataSelectors";

import { InvitationModalDescriptor } from "../descriptors";
import { getInvitations } from "../api";
import { ExpiresIn, InvitationDocumentRoot, invitationDtoToEntry } from "../types";

const INVITATION_DOCUMENT_MODEL_ID = "ApplicationInvitation_D";
const DEFAULT_EXPIRES_IN: ExpiresIn = "HOURS_72";

export const invitationDataProvider: DataProvider = {
	name: "InvitationDataProvider",
	canHandle({ dataHolder, operation }) {
		return InvitationModalDescriptor.isInstance(dataHolder.descriptor) && operation === "load";
	},
	*provideData({ activityId, operation }): SagaGenerator<void> {
		if (operation !== "load") {
			return;
		}

		const activity = yield* select(ActivitySelectors.activityById(activityId));
		assertTrue(InvitationModalDescriptor.isInstance(activity?.descriptor));

		const { applicationKey, sourceAppKeys } = activity.descriptor;

		// Load existing invitations from backend
		let existingInvitations: InvitationDocumentRoot["existingInvitations"] = [];
		try {
			existingInvitations = (yield* call(getInvitations, applicationKey)).map(invitationDtoToEntry);
		} catch (error) {
			console.error("Error loading existing invitations", error);
		}

		// Create empty document for the form
		const emptyDocument = {
			id: NEW_INSTANCE_IDENTIFIER,
			modelId: INVITATION_DOCUMENT_MODEL_ID
		};

		// Check available clients — auto-select when only one option exists
		const availableClients = yield* select(selectAvailableClients(applicationKey));
		const hasPreselectedClient = availableClients.length === 1;
		const emptyClientAllowed = availableClients.includes("");

		// Store form document and existing invitations
		const root: InvitationDocumentRoot = {
			newInvitations: {
				expiresIn: DEFAULT_EXPIRES_IN,
				recipients: [],
				clientPrefilled: hasPreselectedClient,
				emptyClientAllowed,
				...(hasPreselectedClient ? { client: availableClients[0] } : {}),
				contactsAvailable: sourceAppKeys.length > 0
			},
			existingInvitations
		};
		yield* put(
			ActivityActions.setData({
				activityId,
				data: {
					document: {
						...emptyDocument,
						root
					}
				}
			})
		);

		// Load contact data — no document reference available in invitation context, use clients directly.
		// Contacts are filtered to the preselected client or all clients the user may manage.
		const appKeyList = sourceAppKeys
			.split(",")
			.map((s: string) => s.trim())
			.filter(Boolean);

		if (appKeyList.length > 0) {
			const clientsForLoad = hasPreselectedClient ? [availableClients[0]] : availableClients;
			try {
				const enumGroups = yield* call(loadContactDataByClients, appKeyList, clientsForLoad);
				const unresolvedKeys = enumGroups.filter(group => !group.resolved).map(group => group.key);
				const contactData: ContactData = {
					values: Object.fromEntries(enumGroups.map(group => [group.key, group.values])),
					names: Object.fromEntries(
						enumGroups.filter(group => group.name).map(group => [group.key, group.name!])
					)
				};
				const contactDataHolder: Activity.DataHolder = {
					descriptor: { type: "contactData" },
					dirty: false,
					savingState: "saved",
					loadingState: "missing",
					slices: {}
				};
				yield* put(addDataHolder({ dataHolder: contactDataHolder, activityId }));
				yield* put(
					ActivityActions.setDataByDescriptor({
						activityId,
						descriptor: { type: "contactData" },
						data: contactData
					})
				);
				if (unresolvedKeys.length > 0) {
					yield* put(
						createCustomNotificationAction({
							activityId,
							severity: "warning",
							title: localizableFromLocaleResources(RESOURCE_KEYS.features.notification.title.warning),
							message: localizableFromLocaleResources(
								RESOURCE_KEYS.features.externalEnumeration.contactList.warning,
								{ appID: { type: "plain", value: unresolvedKeys.join(", ") } }
							)
						})
					);
				}
			} catch (error) {
				console.error("Error loading contact data for invitation modal", error);
			}
		}
	}
};
